import MoreVertIcon from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import IconButton from '@mui/material/IconButton'
import { isValidElement, ReactNode } from 'react'
import { Link as ReactRouterLink } from 'react-router'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { alpha, darken, Theme } from '@mui/material/styles'
import { default as MuiLink } from '@mui/material/Link'
import Tooltip from '../Tooltip'
import { useActionMenu } from './ActionMenuContext'

interface ActionMenuLink extends ActionMenuOption {
    href?: string
    openInNewTab?: boolean
    /** Use a browser link for downloads and destinations outside the application's router. */
    native?: boolean
}

interface ActionMenuButton extends ActionMenuOption {
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

interface ActionMenuOption {
    icon: ReactNode
    label: string
    isDestructive?: boolean
    disabled?: boolean
    tooltip?: string
    'data-test-id'?: string
}

interface ActionMenuProps {
    options: (ActionMenuLink | ActionMenuButton)[]
    'data-test-id'?: string
    /** Keep existing inline controls while sharing their actions with the row's context menu. */
    children?: ReactNode
}

// A filled red row suits dark themes but overwhelms a light menu, where red text marks the risk instead.
const deleteStyle = (theme: Theme) =>
    theme.palette.mode === 'dark'
        ? {
              backgroundColor: darken(theme.palette.error.light, 0.5),
              color: theme.palette.common.white,
              '&:hover': {
                  backgroundColor: darken(theme.palette.error.light, 0.5),
                  color: theme.palette.common.white,
              },
          }
        : {
              color: theme.palette.error.main,
              '& .MuiListItemIcon-root': { color: theme.palette.error.main },
              '&:hover': { backgroundColor: alpha(theme.palette.error.main, theme.palette.action.hoverOpacity) },
          }

export const ActionMenu = (props: ActionMenuProps) => {
    const available = props.options.some((option) => 'onClick' in option || ('href' in option && !!option.href))
    const { open, menuProps, handleClick, handleClose } = useActionMenu(available)

    const renderOptionContent = (option: ActionMenuLink | ActionMenuButton) => {
        const { icon, label } = option
        return (
            <>
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={label} />
            </>
        )
    }

    const wrapWithTooltip = (element: ReactNode, tooltip?: string) => {
        if (!tooltip) return element
        return (
            <Tooltip key={isValidElement(element) ? element.key : undefined} title={tooltip} disablePortal={false}>
                <span>{element}</span>
            </Tooltip>
        )
    }

    return (
        <>
            {props.children !== undefined ? (
                props.children
            ) : (
                <IconButton
                    aria-label="open action menu"
                    data-test-id={props['data-test-id'] ? `open-${props['data-test-id']}` : 'open-action-menu'}
                    aria-haspopup="true"
                    onClick={handleClick}
                >
                    <MoreVertIcon />
                </IconButton>
            )}
            <Menu
                {...menuProps}
                data-test-id={props['data-test-id'] || 'action-menu'}
                data-testid={props['data-test-id'] || 'action-menu'}
                data-is-open={open ? 'true' : 'false'}
                keepMounted
            >
                {props.options.map((option, index) => {
                    // Tries to use the most "native" way to open a link

                    if ('onClick' in option) {
                        const { onClick } = option as ActionMenuButton
                        return wrapWithTooltip(
                            <MenuItem
                                key={index}
                                sx={option.isDestructive ? deleteStyle : undefined}
                                data-test-id={option['data-test-id']}
                                data-testid={option['data-test-id']}
                                disabled={option.disabled}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    if (!option.disabled) {
                                        onClick?.(e)
                                    }
                                    handleClose(e)
                                }}
                            >
                                {renderOptionContent(option)}
                            </MenuItem>,
                            option.tooltip
                        )
                    }

                    if (!('href' in option)) return null
                    const { href, openInNewTab, native } = option as ActionMenuLink
                    if (!href) return null

                    if (!openInNewTab && !native) {
                        return wrapWithTooltip(
                            <MenuItem
                                key={index}
                                sx={option.isDestructive ? deleteStyle : undefined}
                                data-test-id={option['data-test-id']}
                                data-testid={option['data-test-id']}
                                disabled={option.disabled}
                                component={ReactRouterLink}
                                to={href}
                                onClick={handleClose}
                            >
                                {renderOptionContent(option)}
                            </MenuItem>,
                            option.tooltip
                        )
                    }

                    return wrapWithTooltip(
                        <MenuItem
                            key={index}
                            sx={option.isDestructive ? deleteStyle : undefined}
                            data-test-id={option['data-test-id']}
                            data-testid={option['data-test-id']}
                            disabled={option.disabled}
                            component={MuiLink}
                            href={href}
                            target={openInNewTab ? '_blank' : undefined}
                            rel={openInNewTab ? 'noopener noreferrer' : undefined}
                            onClick={handleClose}
                        >
                            {renderOptionContent(option)}
                        </MenuItem>,
                        option.tooltip
                    )
                })}
            </Menu>
        </>
    )
}
